/**
 * The shell's vessel selector — thin wrapper around VesselDropdown from
 * `@syia-ai/ui/domain` (the published @syia-ai/ui). Planned to move to a presentational
 * `VesselSelect` in the Maritime design system plus @syia-ai/maritimeai-vessels.
 *
 * It is deliberately not a bespoke dropdown. That component already owns the whole
 * contract this shell depends on:
 *
 *   - it dispatches `vesselSyncChanged` / `vesselSyncCleared`, which is exactly what
 *     src/lib/vessel-bridge.ts listens for before writing the three localStorage keys and
 *     broadcasting VESSEL_CHANGED to every registered iframe;
 *   - it writes and restores `vesselSync_activeVessel`, `selectedUserVesselId` and
 *     `vesselDropdown_activeGroup` itself, so a selection survives a reload and is the
 *     same selection the remotes read;
 *   - it renders "My Vessels (N)", the group/DOC/fleet levels and the custom-group
 *     tabs — none of which a hand-written control would get right, and all of which the
 *     user already knows from the apps.
 *
 * So this file fetches the vessel list and gets out of the way. In particular it does NOT
 * dispatch the sync event: the dropdown does, and two writers of the same event means the
 * bridge broadcasts twice per selection.
 *
 * `selectedVessel` IS passed, and must be: the component is controlled on it. The value
 * its trigger displays is derived from that prop, so with it absent the trigger shows the
 * placeholder forever — you pick a vessel, the event fires, every remote follows, and the
 * control still reads "My Vessels". It restores the selection itself on mount by calling
 * onVesselChange with the stored vessel; holding that here is what makes the restoration
 * visible.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  VesselDropdown,
  type VesselData,
  type CustomGroup,
} from '@syia-ai/ui/domain';
import { getAuthToken } from '@/utils/authToken';
import { httpFetch } from '@/lib/httpClient';
import { reportError } from '@/observability/clientLogger';

interface VesselSelectorProps {
  tenant: string | null;
}

/**
 * Cached vessel list.
 *
 * /api/user-vessels is three Mongo round trips plus the group-details batch, so on a cold
 * call the dropdown sits disabled on "Loading..." for as long as that takes — on every
 * page load, for a list that changes a few times a year. RouteGuard already solved the
 * same problem the same way (`routeGuardApps`), so this follows it: paint from the cache
 * immediately, then revalidate behind it and swap the result in.
 *
 * sessionStorage, not localStorage: it should survive a reload and in-app navigation, not
 * outlive the tab. Every access is wrapped — it throws in private mode and with site data
 * blocked, and a dropdown must not be what takes the page down.
 */
const CACHE_KEY = 'shellVesselList';
const CACHE_AT_KEY = 'shellVesselListAt';
const CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedVessels {
  vessels: VesselData[];
  customGroups: CustomGroup[];
}

function readCache(): CachedVessels | null {
  try {
    const at = Number(sessionStorage.getItem(CACHE_AT_KEY));
    if (!at || Date.now() - at > CACHE_TTL_MS) return null;
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedVessels) : null;
  } catch {
    return null;
  }
}

function writeCache(value: CachedVessels): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
    sessionStorage.setItem(CACHE_AT_KEY, String(Date.now()));
  } catch {
    // Quota or disabled storage. The next load just pays the request again.
  }
}

export default function VesselSelector({ tenant }: VesselSelectorProps) {
  const cached = useMemo(readCache, []);
  const [vessels, setVessels] = useState<VesselData[]>(cached?.vessels ?? []);
  const [customGroups, setCustomGroups] = useState<CustomGroup[]>(cached?.customGroups ?? []);
  // A cache hit means the control is usable on the first frame; the request still runs,
  // it just does not hold the UI hostage while it does.
  const [loading, setLoading] = useState(!cached);
  // What the trigger displays. Seeded by the component's own restoration, which fires
  // onVesselChange on mount, and updated on every pick after that.
  const [selectedVessel, setSelectedVessel] = useState<VesselData | null>(null);

  useEffect(() => {
    let cancelled = false;

    // No tenant yet (session still resolving, or signed out). Stop LOADING rather than
    // returning under it: `disabled={loading}` would otherwise stay true for the life of
    // the page, and a dropdown that cannot be opened has no visible settings button,
    // no search box and no list — it reads as "the control is broken", not "no session".
    if (!tenant) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        // localhost → Bearer header; deployed → null, the HttpOnly cookie authenticates.
        const authToken = getAuthToken();
        const response = await httpFetch('/api/user-vessels', {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            'x-node': tenant,
          },
        });
        if (!response.ok || cancelled) return;

        const data = await response.json();
        if (cancelled) return;

        // `success: false` arrives with HTTP 200 on the partial-failure path, carrying an
        // empty vessel list. Checking it — as every app call site does — is what stops
        // that being rendered as "this user has no vessels".
        if (data.success === false) {
          reportError(new Error(data.error || 'user-vessels returned success: false'), {
            event_detail: 'vessel_selector_api_error',
          });
          return;
        }

        const freshVessels = (data.vessels ?? []) as VesselData[];
        setVessels(freshVessels);
        // Cache the vessels now rather than waiting on the custom-groups request below:
        // if that one fails, the expensive list is still worth keeping.
        writeCache({ vessels: freshVessels, customGroups: cached?.customGroups ?? [] });

        // Custom groups come from their OWN endpoint, the way the apps load them, and in
        // the background — they must not hold up the vessel list. /api/user-vessels does
        // return a `customGroups` field, but the apps treat /api/custom-groups as the
        // source of truth and the config slideout writes back there, so reading the other
        // copy is how the two drift after a save.
        void httpFetch('/api/custom-groups', {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            'x-node': tenant,
          },
        })
          .then((cg) => (cg.ok ? cg.json() : null))
          .then((cgData) => {
            if (cancelled || !cgData?.success) return;
            const groups = (cgData.customGroups ?? []) as CustomGroup[];
            setCustomGroups(groups);
            writeCache({ vessels: freshVessels, customGroups: groups });
          })
          .catch((error) => reportError(error, { event_detail: 'vessel_selector_custom_groups_failed' }));
      } catch (error) {
        // An empty list renders a disabled-looking dropdown rather than taking the
        // page's chrome down with it.
        reportError(error, { event_detail: 'vessel_selector_fetch_failed' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenant]);

  return (
    <div className="shell-vessel-select">
    <VesselDropdown
      vessels={vessels}
      selectedVessel={selectedVessel}
      customGroups={customGroups}
      loading={loading}
      placeholder="My Vessels"
      // No `ds-w-*` class on purpose. The component STRING-MATCHES its className for w-32/48/
      // 64/96 and writes that width inline, which no stylesheet can override; with none it
      // writes `width: inherit`, so the wrapper below sets the size (160px, per the design).
      className="shell-vessel-dropdown"
      // The vessel-config slideout — "Configure vessels" and custom groups. Its routes
      // (/api/vessels/all, /api/custom-groups, /api/user-vessels/update) are served by
      // THIS app, so shellOrigin is our own origin and every call is same-origin: no
      // preflight, and no third-party cookie for Safari's ITP to withhold.
      configurable
      // Both, and both pointing at the shell — the same pairing every app call site uses.
      // `apiBaseUrl` is deprecated but is still the READ fallback inside the component,
      // so omitting it leaves the config slideout reading from the wrong base.
      apiBaseUrl={typeof window === 'undefined' ? '' : window.location.origin}
      shellOrigin={typeof window === 'undefined' ? '' : window.location.origin}
      onCustomGroupsUpdated={setCustomGroups}
      onVesselsUpdated={setVessels}
      // Holds the selection so the trigger can show it. Deliberately nothing else: the
      // dropdown has already dispatched `vesselSyncChanged` by the time this runs, and
      // vessel-bridge.ts is listening for it and owns the storage writes and the iframe
      // broadcast. Writing any of that here would be a second writer of the same state.
      onVesselChange={(vessel) => setSelectedVessel(vessel)}
    />
    </div>
  );
}
